import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import api from '../utils/api';

function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  
  const [productData, setProductData] = useState({
    title: '',
    description: '',
    category: 'lunchbox',
    age_range: '3-6',
    price: '',
    original_price: '',
    colors: [],
    materials: [],
    features: [],
    image_urls: [],
    stock_count: 100,
  });

  const handleLogin = () => {
    if (token === 'tiffin-admin-secret-2026') {
      localStorage.setItem('admin_token', token);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
      window.location.href = '/admin/dashboard';
    } else {
      toast.error('Invalid admin token');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      // Get upload URL
      const uploadData = await api.adminGetCloudflareUploadUrl(token);
      
      // Upload to Cloudflare
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await fetch(uploadData.upload_url, {
        method: 'POST',
        body: formData,
      });

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        const imageUrl = `https://imagedelivery.net/${result.result.id}/public`;
        setProductData(prev => ({
          ...prev,
          image_urls: [...prev.image_urls, imageUrl],
        }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleAIExtract = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExtracting(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        const result = await api.adminAIExtract(base64, token);
        
        if (result.success) {
          const data = result.data;
          setProductData(prev => ({
            ...prev,
            title: data.title || prev.title,
            description: data.description || prev.description,
            category: data.category || prev.category,
            age_range: data.age_range || prev.age_range,
            price: data.price_suggestion || prev.price,
            colors: data.colors || prev.colors,
            materials: data.materials || prev.materials,
            features: data.features || prev.features,
          }));
          toast.success('Product data extracted successfully!');
        } else {
          toast.error('AI extraction failed');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('AI extraction error:', error);
      toast.error('Failed to extract product data');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.adminCreateProduct(productData, token);
      toast.success('Product created successfully!');
      setProductData({
        title: '',
        description: '',
        category: 'lunchbox',
        age_range: '3-6',
        price: '',
        original_price: '',
        colors: [],
        materials: [],
        features: [],
        image_urls: [],
        stock_count: 100,
      });
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error('Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('admin_token') === 'tiffin-admin-secret-2026') {
      window.location.href = '/admin/dashboard';
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-3 sm:px-4">
        <Card className="p-6 sm:p-8 w-full max-w-md">
          <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Admin Login</h1>
          <div className="space-y-4">
            <div>
              <Label className="text-sm sm:text-base">Admin Token</Label>
              <Input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter admin secret key"
                className="text-sm sm:text-base"
              />
            </div>
            <Button onClick={handleLogin} className="w-full">
              Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-8 px-3 sm:px-4 pb-20 md:pb-8" data-testid="admin-page">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-2xl sm:text-3xl font-display font-normal mb-4 sm:mb-8">Admin Panel</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/admin/marketing'}>
            <h2 className="text-lg sm:text-xl font-semibold mb-2">Marketing & Ads</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Configure Facebook Pixel and create automated ad campaigns</p>
          </Card>
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-2">Products</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Manage products with AI-powered extraction</p>
          </Card>
        </div>

        <Card className="p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4">AI Product Extraction</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ai-extract">Upload Product Image for AI Analysis</Label>
              <Input
                id="ai-extract"
                type="file"
                accept="image/*"
                onChange={handleAIExtract}
                disabled={extracting}
                data-testid="admin-ai-extract-upload"
              />
              {extracting && (
                <p className="text-sm text-muted-foreground mt-2">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                  Extracting product data...
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Add New Product</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={productData.title}
                  onChange={(e) => setProductData({ ...productData, title: e.target.value })}
                  required
                  data-testid="admin-product-title"
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  className="w-full h-10 rounded-md border border-input bg-background px-3"
                  value={productData.category}
                  onChange={(e) => setProductData({ ...productData, category: e.target.value })}
                >
                  <option value="lunchbox">Lunchbox</option>
                  <option value="bag">School Bag</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={productData.description}
                onChange={(e) => setProductData({ ...productData, description: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="price">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={productData.price}
                  onChange={(e) => setProductData({ ...productData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="original_price">Original Price (₹)</Label>
                <Input
                  id="original_price"
                  type="number"
                  value={productData.original_price}
                  onChange={(e) => setProductData({ ...productData, original_price: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="age_range">Age Range</Label>
                <select
                  id="age_range"
                  className="w-full h-10 rounded-md border border-input bg-background px-3"
                  value={productData.age_range}
                  onChange={(e) => setProductData({ ...productData, age_range: e.target.value })}
                >
                  <option value="3-6">3-6 years</option>
                  <option value="7-10">7-10 years</option>
                  <option value="11-15">11-15 years</option>
                  <option value="3-12">3-12 years (All Kids)</option>
                  <option value="5-10">5-10 years</option>
                  <option value="6-12">6-12 years</option>
                  <option value="all">All Ages</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="image-upload">Upload Images</Label>
              <Input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={loading}
              />
              {productData.image_urls.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {productData.image_urls.map((url, index) => (
                    <img key={index} src={url} alt="Product" className="w-20 h-20 object-cover rounded-md" />
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="admin-create-product">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Product'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default AdminPage;